import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Dtc, DtcSeverity, PidReading, ReadinessReport } from '@tendedloop/shared';
import { Layout } from '@/components/Layout';
import { Button, Card, StatusPill, ThinkingState, type StatusTone } from '@/components/ui';
import { Icon } from '@/components/kol/Icon';
import { useObd } from '@/hooks/useObd';

const SCOUT_PID_KEYS = [
  'engineRpm',
  'vehicleSpeed',
  'coolantTemp',
  'fuelLevel',
  'controlModuleVoltage',
  'engineOilTemp',
  'ambientAirTemp',
  'engineLoad',
];

function severityTone(s: DtcSeverity | undefined): StatusTone {
  if (s === 'stop_safely') return 'danger';
  if (s === 'service_soon') return 'warning';
  return 'info';
}

export function ObdDiagnosticsPage() {
  const { t } = useTranslation();
  const { id: vehicleId } = useParams<{ id: string }>();
  const { status, progress, result, error, saved, connectAndScan, reset } = useObd(vehicleId ?? '');

  return (
    <Layout
      title={t('obd.title', { defaultValue: 'אבחון רכב' })}
      trailing={
        <Link
          to={vehicleId ? `/kol/vehicles/${vehicleId}` : '/'}
          aria-label={t('common.back', { defaultValue: 'חזרה' })}
          className="w-10 h-10 rounded-full border border-carnaf-line bg-carnaf-paper-elevated grid place-items-center text-carnaf-ink-muted shadow-kol-1"
        >
          <Icon name="arrow" size={19} />
        </Link>
      }
    >
      {status === 'unsupported' && <UnsupportedNotice />}

      {status === 'idle' && (
        <div className="space-y-5">
          <Card className="text-center p-6">
            <div
              className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-carnaf-teal-soft text-carnaf-teal grid place-items-center"
              aria-hidden
            >
              <Icon name="bluetooth" size={30} />
            </div>
            <h2 className="text-h3 text-carnaf-ink mb-2">
              {t('obd.idle_title', { defaultValue: 'חברו מתאם OBD לרכב' })}
            </h2>
            <p className="text-body text-carnaf-ink-muted leading-relaxed">
              {t('obd.idle_sub', {
                defaultValue: 'חברו מתאם ELM327 ליציאת ה-OBD ברכב, הפעילו את המנוע, ולחצו לחיבור.',
              })}
            </p>
          </Card>
          <Button variant="primary" size="lg" fullWidth onClick={connectAndScan}>
            {t('obd.connect', { defaultValue: 'התחברו וסרקו' })}
          </Button>
        </div>
      )}

      {(status === 'connecting' || status === 'scanning') && (
        <div className="py-6">
          <ThinkingState
            status={[
              t('obd.connecting', { defaultValue: 'מתחברים למתאם…' }),
              t('obd.phase.codes', { defaultValue: 'קוראים קודי תקלה…' }),
              t('obd.phase.readiness', { defaultValue: 'בודקים מוכנות לטסט…' }),
              t('obd.phase.live', { defaultValue: 'קוראים נתונים חיים…' }),
            ]}
          />
          {progress && (
            <div className="mt-4">
              <div className="h-2 rounded-full bg-carnaf-paper-sunk overflow-hidden">
                <div
                  className="h-full bg-carnaf-teal transition-all"
                  style={{ width: `${Math.round(progress.fraction * 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {status === 'error' && (
        <div className="space-y-4">
          <div
            role="alert"
            className="rounded-md bg-carnaf-red-soft border border-carnaf-red p-4 text-carnaf-red text-body-sm"
          >
            {error ?? t('common.error', { defaultValue: 'משהו השתבש' })}
          </div>
          <Button variant="secondary" size="md" fullWidth onClick={connectAndScan}>
            {t('obd.retry', { defaultValue: 'נסו שוב' })}
          </Button>
        </div>
      )}

      {status === 'done' && result && (
        <div className="space-y-4 pb-8">
          {result.readiness && <ReadinessCard readiness={result.readiness} />}

          <DtcSection
            title={t('obd.stored_dtcs', { defaultValue: 'קודי תקלה פעילים' })}
            emptyLabel={t('obd.no_stored', { defaultValue: 'אין קודי תקלה פעילים. ' })}
            dtcs={result.storedDtcs}
          />

          {result.pendingDtcs.length > 0 && (
            <DtcSection
              title={t('obd.pending_dtcs', { defaultValue: 'קודים ממתינים' })}
              emptyLabel=""
              dtcs={result.pendingDtcs}
            />
          )}

          <LiveDataCard pids={result.pids} />

          {result.vin && (
            <p className="text-[15px] text-carnaf-ink-muted text-center">
              {t('obd.vin_read', { defaultValue: 'מספר שלדה מהרכב' })}: {result.vin}
            </p>
          )}

          <div className="flex flex-col gap-2 pt-2">
            {(result.storedDtcs.length > 0 || result.pendingDtcs.length > 0) && (
              <Link to={vehicleId ? `/kol/vehicles/${vehicleId}/ask` : '#'}>
                <Button variant="primary" size="md" fullWidth>
                  {t('obd.ask_about_codes', { defaultValue: 'שאלו את הרכב על הקודים' })}
                </Button>
              </Link>
            )}
            <Button variant="ghost" size="md" fullWidth onClick={reset}>
              {t('obd.scan_again', { defaultValue: 'סריקה חדשה' })}
            </Button>
          </div>

          <p className="text-[15px] text-carnaf-ink-faint text-center">
            {saved
              ? t('obd.saved', { defaultValue: 'הסריקה נשמרה' })
              : t('obd.not_saved', { defaultValue: 'הסריקה לא נשמרה (אפשר לסרוק שוב)' })}
            {result.adapterName ? ` · ${result.adapterName}` : ''}
          </p>
        </div>
      )}
    </Layout>
  );
}

function ReadinessCard({ readiness }: { readiness: ReadinessReport }) {
  const { t } = useTranslation();
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between gap-3 mb-2">
        <h3 className="text-body-lg font-semibold text-carnaf-ink">
          {t('obd.readiness_title', { defaultValue: 'מוכנות לטסט שנתי' })}
        </h3>
        <StatusPill tone={readiness.readyForInspection ? 'success' : 'warning'} withDot>
          {readiness.readyForInspection
            ? t('obd.ready', { defaultValue: 'מוכן' })
            : t('obd.not_ready', { defaultValue: 'לא מוכן' })}
        </StatusPill>
      </div>
      {readiness.milOn && (
        <p className="text-body-sm text-carnaf-red mb-1">
          {t('obd.mil_on', { defaultValue: 'נורת ה-Check Engine דולקת' })}
        </p>
      )}
      {readiness.readyForInspection ? (
        <p className="text-body-sm text-carnaf-ink-muted">
          {t('obd.ready_explain', {
            defaultValue: 'כל בדיקות הפליטה הושלמו. הרכב צפוי לעבור את חלק ה-OBD בטסט.',
          })}
        </p>
      ) : (
        <div>
          <p className="text-body-sm text-carnaf-ink-muted mb-1">
            {t('obd.not_ready_explain', {
              defaultValue: 'בדיקות פליטה שטרם הושלמו (עלולות להכשיל בטסט). סעו 30-50 ק״מ של נהיגה מעורבת.',
            })}
          </p>
          <ul className="text-body-sm text-carnaf-ink list-disc list-inside">
            {readiness.notReady.map((m) => (
              <li key={m.id}>{m.name}</li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

function DtcSection({ title, emptyLabel, dtcs }: { title: string; emptyLabel: string; dtcs: Dtc[] }) {
  if (dtcs.length === 0) {
    if (!emptyLabel) return null;
    return (
      <Card className="p-4">
        <h3 className="text-body-lg font-semibold text-carnaf-ink mb-1">{title}</h3>
        <p className="text-body-sm text-carnaf-ink-muted">{emptyLabel}</p>
      </Card>
    );
  }
  return (
    <div className="space-y-2">
      <h3 className="text-body-lg font-semibold text-carnaf-ink">{title}</h3>
      {dtcs.map((d) => (
        <DtcRow key={`${d.status}-${d.code}`} dtc={d} />
      ))}
    </div>
  );
}

function DtcRow({ dtc }: { dtc: Dtc }) {
  const { t } = useTranslation();
  const tone = severityTone(dtc.severity);
  const severityLabel =
    dtc.severity === 'stop_safely'
      ? t('obd.sev.stop', { defaultValue: 'עצרו בבטחה' })
      : dtc.severity === 'service_soon'
        ? t('obd.sev.soon', { defaultValue: 'טפלו בקרוב' })
        : t('obd.sev.info', { defaultValue: 'מידע' });
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between gap-3 mb-1">
        <span className="font-mono text-body font-semibold text-carnaf-ink" dir="ltr">
          {dtc.code}
        </span>
        <StatusPill tone={tone}>{severityLabel}</StatusPill>
      </div>
      {dtc.title && <div className="text-body-sm font-medium text-carnaf-ink">{dtc.title}</div>}
      {dtc.meaning && <p className="text-body-sm text-carnaf-ink-muted leading-relaxed mt-1">{dtc.meaning}</p>}
      {dtc.commonCauses && dtc.commonCauses.length > 0 && (
        <details className="mt-2">
          <summary className="text-body-sm text-carnaf-teal-pressed cursor-pointer">
            {t('obd.common_causes', { defaultValue: 'סיבות נפוצות' })}
          </summary>
          <ul className="text-body-sm text-carnaf-ink-muted list-disc list-inside mt-1">
            {dtc.commonCauses.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}

function LiveDataCard({ pids }: { pids: PidReading[] }) {
  const { t } = useTranslation();
  if (pids.length === 0) return null;
  const ordered = [...pids].sort((a, b) => {
    const ai = SCOUT_PID_KEYS.indexOf(a.key);
    const bi = SCOUT_PID_KEYS.indexOf(b.key);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
  return (
    <Card className="p-4">
      <h3 className="text-body-lg font-semibold text-carnaf-ink mb-3">
        {t('obd.live_data', { defaultValue: 'נתונים חיים' })}
      </h3>
      <div className="grid grid-cols-2 gap-3">
        {ordered.map((p) => (
          <div key={p.key} className="rounded-md bg-carnaf-paper-sunk p-3">
            <div className="text-[15px] text-carnaf-ink-muted">{t(`obd.pid.${p.key}`, { defaultValue: p.name })}</div>
            <div className="text-body-lg font-semibold text-carnaf-ink" dir="ltr">
              {p.value}
              <span className="text-body-sm text-carnaf-ink-muted ms-1">{p.unit}</span>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function UnsupportedNotice() {
  const { t } = useTranslation();
  return (
    <Card className="p-6 text-center">
      <div className="text-5xl mb-3" aria-hidden>
        <Icon name="warning" size={28} />
      </div>
      <h2 className="text-h3 text-carnaf-ink mb-2">
        {t('obd.unsupported_title', { defaultValue: 'אבחון OBD לא נתמך בדפדפן זה' })}
      </h2>
      <p className="text-body text-carnaf-ink-muted leading-relaxed mb-3">
        {t('obd.unsupported_sub', {
          defaultValue:
            'חיבור Bluetooth למתאם OBD עובד ב-Chrome על אנדרואיד. ב-iPhone השתמשו בדפדפן Bluefy מה-App Store.',
        })}
      </p>
      <a
        href="https://apps.apple.com/us/app/bluefy-web-ble-browser/id1492822055"
        target="_blank"
        rel="noopener noreferrer"
        className="text-body text-carnaf-teal-pressed underline"
      >
        {t('obd.get_bluefy', { defaultValue: 'הורידו את Bluefy ל-iPhone' })}
      </a>
    </Card>
  );
}
